import { makeVar, useReactiveVar } from "@apollo/client";
import { activeMemberships } from "lib/apollo/constants";
import type { ActiveMembershipIdsType } from "lib/apollo/types";
import type { User } from "@shared/utils.user";
// eslint-disable-next-line import/no-cycle
import { getLocale } from "./apolloClient";
import type {
  CheckoutStateType,
  ItemInput,
  UserType
} from "@shared/graphql.types";
// eslint-disable-next-line import/no-cycle
import { toItemInput } from "./apollo/conversions";

function getMembershipIds(): ActiveMembershipIdsType {
  return activeMemberships[getLocale()];
}

export type ExtendedUserType = UserType & {
  isLoggedIn: boolean;
  fullName: string;
};
export function BPUser(user: ExtendedUserType): User {
  return {
    ...(user || {}),
    get fullName(): string {
      return [user?.firstName, user?.lastName].join(" ").trim();
    },
    get basketItemInputs(): ItemInput[] {
      return user?.basket?.map(toItemInput) || [];
    },
    get wishlistItemInputs(): ItemInput[] {
      return user?.wishlist?.map(toItemInput) || [];
    },
    get basketCount(): number {
      return (
        user?.basket?.reduce((acc, item) => {
          return item?.variant?.availability?.status === "instock" ||
            item?.variant === null
            ? acc + item.quantity
            : acc;
        }, 0) || 0
      );
    },
    get isMember(): boolean {
      const email = user?.email;
      const membership = user?.membership;
      return !!(
        membership &&
        (membership.id !== "none:none" || email?.endsWith("@test.com"))
      );
    },
    get hasMembershipInBag(): boolean {
      return user?.basket?.some(({ type }) => type === "membership");
    },
    get hasGiftMembershipInBag(): boolean {
      return user?.basket?.some(({ type }) => type === "giftMembership");
    },
    get hasMembership(): boolean {
      return this.isMember || this.hasMembershipInBag;
    },
    get effectiveMembership() {
      if (user.membership?.id !== "none:none") {
        return user.membership;
      }
      return user?.basket?.find(({ type }) => type === "membership")
        ?.membership;
    },
    get hasFreeTrialMembership(): boolean {
      const {
        freeTrialMembershipId,
        freeTrialMembership30Id,
        freeTrialLimitlessMembership30Id,
        threeMonthFreeTrialMembershipId,
        thirtyDayFreeTrialMembershipId,
        thirtyDayFreeTrialRenewsToMonthlyMembershipId,
        sixtyDayFreeTrialRenewsToMonthlyMembershipId,
        sixtyDayFreeTrialRenewsToAnnualMembershipId
      } = getMembershipIds();
      const id = this.effectiveMembership?.id;
      return !!(
        id &&
        [
          freeTrialMembershipId,
          freeTrialMembership30Id,
          freeTrialLimitlessMembership30Id,
          threeMonthFreeTrialMembershipId,
          thirtyDayFreeTrialMembershipId,
          thirtyDayFreeTrialRenewsToMonthlyMembershipId,
          sixtyDayFreeTrialRenewsToMonthlyMembershipId,
          sixtyDayFreeTrialRenewsToAnnualMembershipId
        ].includes(id)
      );
    },
    get hasFreeTrialMembershipInBag(): boolean {
      const {
        freeTrialMembershipId,
        freeTrialMembership30Id,
        freeTrialLimitlessMembership30Id,
        threeMonthFreeTrialMembershipId,
        thirtyDayFreeTrialMembershipId,
        thirtyDayFreeTrialRenewsToMonthlyMembershipId,
        sixtyDayFreeTrialRenewsToMonthlyMembershipId,
        sixtyDayFreeTrialRenewsToAnnualMembershipId
      } = getMembershipIds();
      return user?.basket?.some(
        (item) =>
          item.type === "membership" &&
          item.membership?.id &&
          [
            freeTrialMembershipId,
            freeTrialMembership30Id,
            freeTrialLimitlessMembership30Id,
            threeMonthFreeTrialMembershipId,
            thirtyDayFreeTrialMembershipId,
            thirtyDayFreeTrialRenewsToMonthlyMembershipId,
            sixtyDayFreeTrialRenewsToMonthlyMembershipId,
            sixtyDayFreeTrialRenewsToAnnualMembershipId
          ].includes(item.membership?.id)
      );
    },
    get hasPlusMembership(): boolean {
      return (
        !!this.effectiveMembership?.name?.toUpperCase().includes("PLUS") ||
        !!this.effectiveMembership?.id?.toUpperCase().includes("PLUS")
      );
    },
    get hasPlusMembershipInBag(): boolean {
      return user?.basket?.some(
        (item) =>
          item.type === "membership" &&
          item.membership?.name?.toUpperCase().includes("PLUS")
      );
    },
    get hasStaffMembership() {
      const { staffMembershipId } = getMembershipIds();
      return (
        user.membership?.id === staffMembershipId ||
        !!this.effectiveMembership?.id.toUpperCase().includes("STAFF")
      );
    }
  };
}

export const guestUser: ExtendedUserType = {
  basket: [],
  carriedForward: 0,
  discounts: [],
  email: "guest@bp.com",
  hints: [],
  id: "initial",
  name: "guest",
  firstName: "",
  lastName: "",
  reviews: [],
  spentSoFar: 0,
  wishlist: [],
  includedDiscounts: [],
  rejectedDiscounts: [],
  isFreeTrialAllowed: true,
  isLoggedIn: false,
  fullName: ""
};

export function getUser(): User {
  if (typeof localStorage === "undefined") return BPUser(guestUser);
  const x = localStorage.getItem("user");
  return BPUser(x ? JSON.parse(x) : guestUser);
}

export const userVar = makeVar<User>(getUser());

const emptyCheckoutState: CheckoutStateType = {
  cartId: "",
  cartKey: "",
  cartVersion: -1,
  deliveryMethod: "None",
  discounts: [],
  includedDiscounts: [],
  rejectedDiscounts: [],
  expressCheckoutAvailable: false
};

export const checkoutVar = makeVar<CheckoutStateType>(emptyCheckoutState);

export function setUser(v: Partial<User>): User | Record<string, never> {
  userVar(BPUser({ ...userVar(), ...v }));
  const user = userVar();
  if (typeof localStorage !== "undefined")
    localStorage.setItem("user", JSON.stringify(user));

  return user;
}

export const setCheckout = (v: Partial<CheckoutStateType>): CheckoutStateType =>
  checkoutVar({ ...checkoutVar(), ...v });

export const getPayment = (): CheckoutStateType["payment"] =>
  checkoutVar().payment;

export function clearUser(): void {
  userVar(BPUser(guestUser));
  if (typeof localStorage !== "undefined") localStorage.removeItem("user");
}

export function clearBasket(): void {
  setUser({
    basket: [],
    pricing: null,
    basketCount: 0,
    includedDiscounts: [],
    giftMembershipDetails: undefined
  });
}

export function useUser(): User {
  return useReactiveVar(userVar) || BPUser(guestUser);
}

export function useCheckoutState(): CheckoutStateType {
  return useReactiveVar(checkoutVar) || null;
}
